import React, { useEffect, useState, useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { getTheme } from '../App';
import { useWS } from '../WSContext';
import PerfReports from '../components/PerfReports';

interface Trade {
  id: string; symbol: string; side: string; price: number;
  quantity: number; realizedPnl: number; fee: number;
  executedAtMillis: number; type: string;
}
interface FundingFee {
  id: string; symbol: string; fundingFee: number;
  paymentType: string; createdTime: number;
}
interface Props { dark?: boolean; }

const DATE_SHORTCUTS = [
  { label: 'Bugün',   days: 1 },
  { label: '1 Hafta', days: 7 },
  { label: '1 Ay',    days: 30 },
  { label: 'Tümü',    days: 0 },
];

type SortDir = 'desc' | 'asc';

function fmtSym(s: string) {
  return s.replace('PERP_','').replace('_USDC','').replace('-PERP','');
}

export default function Performance({ dark = true }: Props) {
  const { connected, sendWs, subscribe, activeBotId } = useWS() as any;
  const [trades, setTrades]           = useState<Trade[]>([]);
  const [fundingFees, setFundingFees] = useState<FundingFee[]>([]);
  const [days, setDays]               = useState(7);
  const [symbol, setSymbol]           = useState('');
  const [side, setSide]               = useState('');
  const [sortDir, setSortDir]         = useState<SortDir>('desc');
  const [tradesOpen,  setTradesOpen]  = useState(true);
  const [fundingOpen, setFundingOpen] = useState(true);
  // Günlük rapor arşivi (30/09/2026) — mevcut işlem görünümü 'İşlemler' sekmesinde aynen duruyor
  const [view, setView] = useState<'reports' | 'trades'>('reports');
  const t = getTheme(dark);

  useEffect(() => {
    if (!connected) return;
    setTrades([]);
    setFundingFees([]);
    sendWs({ type: 'get_trades',       payload: { limit: 500 } });
    sendWs({ type: 'get_funding_fees', payload: { limit: 500 } });
  }, [connected, activeBotId, sendWs]);

  useEffect(() => {
    return subscribe((data: any) => {
      if (data.type === 'trades')       setTrades(data.payload || []);
      if (data.type === 'funding_fees') setFundingFees(data.payload || []);
    });
  }, [subscribe]);

  const cutoff = days > 0 ? Date.now() - days * 86_400_000 : 0;

  const coinOptions = useMemo(() => {
    const seen = new Set<string>();
    trades.forEach(tr => tr.symbol && seen.add(tr.symbol));
    fundingFees.forEach(f => f.symbol && seen.add(f.symbol));
    return Array.from(seen).sort();
  }, [trades, fundingFees]);

  const filtered = useMemo(() => {
    const base = trades.filter(tr => {
      if (symbol && tr.symbol !== symbol) return false;
      if (side   && tr.side   !== side)   return false;
      if (cutoff && tr.executedAtMillis < cutoff) return false;
      return true;
    });
    return base.slice().sort((a, b) =>
      sortDir === 'desc'
        ? b.executedAtMillis - a.executedAtMillis
        : a.executedAtMillis - b.executedAtMillis
    );
  }, [trades, symbol, side, cutoff, sortDir]);

  const filteredFunding = useMemo(() =>
    fundingFees.filter(f => {
      if (symbol && f.symbol !== symbol) return false;
      if (side) {
        const received = (f.fundingFee ?? 0) >= 0;
        if (side === 'BUY'  && !received) return false;
        if (side === 'SELL' && received)  return false;
      }
      if (cutoff && f.createdTime < cutoff) return false;
      return true;
    }).sort((a, b) =>
      sortDir === 'desc'
        ? b.createdTime - a.createdTime
        : a.createdTime - b.createdTime
    ),
  [fundingFees, symbol, side, cutoff, sortDir]);

  const totalPnl     = filtered.reduce((s, tr) => s + (Number(tr.realizedPnl) || 0), 0);
  const totalFunding = filteredFunding.reduce((s, f) => s + (Number(f.fundingFee) || 0), 0);
  const winCount     = filtered.filter(tr => (tr.realizedPnl || 0) > 0).length;
  const winRate      = filtered.length > 0 ? (winCount / filtered.length * 100) : 0;

  const card      = `${t.card} border ${t.cardBorder} rounded-xl`;
  const inp       = `border rounded-lg px-2 py-1.5 text-xs focus:outline-none ${t.inp}`;
  const sortArrow = sortDir === 'desc' ? '↓' : '↑';

  const pnlColor     = totalPnl     >= 0 ? t.positive : t.negative;
  const fundingColor = totalFunding >= 0 ? t.positive : t.negative;

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <h2 className={`text-xl font-bold ${t.text}`}>Performans</h2>
        <div className={`flex items-center gap-2 text-xs ${t.muted}`}>
          <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-rose-400'}`} />
          {connected ? 'Canlı' : 'Bağlantı bekleniyor...'}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {([['reports', 'Günlük Raporlar'], ['trades', 'İşlemler']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setView(k)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              view === k ? 'bg-[#4A9FE8] text-[#061530] font-bold' : t.btnSecondary}`}>{l}</button>
        ))}
      </div>

      {view === 'reports' ? <PerfReports t={t} card={card} /> : <>

      {/* Filtreler */}
      <div className="flex items-center gap-3 flex-wrap">
        {DATE_SHORTCUTS.map(s => (
          <button key={s.days} onClick={() => setDays(s.days)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              days === s.days ? 'bg-[#4A9FE8] text-[#061530] font-bold' : t.btnSecondary
            }`}>{s.label}</button>
        ))}
        <select value={symbol} onChange={e => setSymbol(e.target.value)} className={`${inp} w-44 cursor-pointer`}>
          <option value=''>Tüm Coinler</option>
          {coinOptions.map(sym => (
            <option key={sym} value={sym}>{fmtSym(sym)}</option>
          ))}
        </select>
        <select value={side} onChange={e => setSide(e.target.value)} className={`${inp} w-32 cursor-pointer`}>
          <option value=''>Tüm Yönler</option>
          <option value='BUY'>Long (BUY)</option>
          <option value='SELL'>Short (SELL)</option>
        </select>
        {(symbol || side) && (
          <button onClick={() => { setSymbol(''); setSide(''); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${t.btnSecondary}`}>
            ✕ Sıfırla
          </button>
        )}
      </div>

      {/* Özet kartlar */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Toplam PnL',     value: `${totalPnl >= 0 ? '+' : ''}$${totalPnl.toFixed(4)}`,         pos: totalPnl >= 0 },
          { label: 'Toplam İşlem',   value: `${filtered.length}` },
          { label: 'Win Rate',       value: `${winRate.toFixed(1)}%`,                                      pos: winRate >= 50 },
          { label: 'Toplam Funding', value: `${totalFunding >= 0 ? '+' : ''}$${totalFunding.toFixed(4)}`,  pos: totalFunding >= 0 },
        ].map(c => (
          <div key={c.label} className={`${card} p-4`}>
            <p className={`text-xs mb-1 ${t.muted}`}>{c.label}</p>
            <p className={`text-xl font-bold ${(c as any).pos !== undefined ? ((c as any).pos ? t.positive : t.negative) : t.text}`}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* ── İşlem Geçmişi ── */}
      <div className={`${card} overflow-hidden`}>
        <button
          onClick={() => setTradesOpen(v => !v)}
          className={`w-full px-4 py-3 flex items-center justify-between hover:opacity-80 transition-opacity ${tradesOpen ? `border-b ${t.cardBorder}` : ''}`}
        >
          <div className="flex items-center gap-3">
            <span className={t.muted}>{tradesOpen ? <ChevronDown size={15}/> : <ChevronRight size={15}/>}</span>
            <h3 className={`text-sm font-semibold ${t.text}`}>İşlem Geçmişi</h3>
            <span className={`text-xs ${t.muted}`}>{filtered.length} işlem</span>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className={t.muted}>Toplam PnL:</span>
            <span className={`font-bold font-mono ${pnlColor}`}>
              {totalPnl >= 0 ? '+' : ''}${totalPnl.toFixed(4)}
            </span>
          </div>
        </button>

        {tradesOpen && (
          filtered.length === 0 ? (
            <div className={`p-8 text-center ${t.muted} text-xs`}>
              {connected ? 'İşlem bulunamadı.' : 'Backend bağlantısı bekleniyor...'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className={`${t.tableHeader} border-b`}>
                    <th
                      className="px-4 py-2.5 font-medium text-left cursor-pointer select-none hover:opacity-80 transition-opacity"
                      onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                    >Tarih {sortArrow}</th>
                    {['Saat','Sembol','Yön','Fiyat','Miktar','PnL','Ücret'].map((h, i) => (
                      <th key={h} className={`px-4 py-2.5 font-medium ${i < 2 ? 'text-left' : 'text-right'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((tr, idx) => {
                    const d = new Date(tr.executedAtMillis);
                    return (
                      <tr key={tr.id || idx} className={`border-b ${t.tableRow}`}>
                        <td className={`px-4 py-2 ${t.muted}`}>{d.toLocaleDateString('tr-TR')}</td>
                        <td className={`px-4 py-2 ${t.muted}`}>{d.toLocaleTimeString('tr-TR')}</td>
                        <td className={`px-4 py-2 font-semibold ${t.gold}`}>{fmtSym(tr.symbol)}</td>
                        <td className="px-4 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                            tr.side === 'BUY' ? 'bg-emerald-900/50 text-emerald-400' : 'bg-rose-900/50 text-rose-400'
                          }`}>{tr.side}</span>
                        </td>
                        <td className={`px-4 py-2 text-right font-mono ${t.text}`}>${tr.price?.toFixed(4)}</td>
                        <td className={`px-4 py-2 text-right font-mono ${t.text}`}>{tr.quantity}</td>
                        <td className={`px-4 py-2 text-right font-mono font-bold ${(tr.realizedPnl || 0) >= 0 ? t.positive : t.negative}`}>
                          {(tr.realizedPnl || 0) >= 0 ? '+' : ''}${(tr.realizedPnl || 0).toFixed(4)}
                        </td>
                        <td className={`px-4 py-2 text-right font-mono ${t.negative}`}>-${(tr.fee || 0).toFixed(6)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* ── Funding Fee Geçmişi ── */}
      <div className={`${card} overflow-hidden`}>
        <button
          onClick={() => setFundingOpen(v => !v)}
          className={`w-full px-4 py-3 flex items-center justify-between hover:opacity-80 transition-opacity ${fundingOpen ? `border-b ${t.cardBorder}` : ''}`}
        >
          <div className="flex items-center gap-3">
            <span className={t.muted}>{fundingOpen ? <ChevronDown size={15}/> : <ChevronRight size={15}/>}</span>
            <h3 className={`text-sm font-semibold ${t.text}`}>Funding Fee Geçmişi</h3>
            <span className={`text-xs ${t.muted}`}>{filteredFunding.length} kayıt</span>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className={t.muted}>Toplam Funding:</span>
            <span className={`font-bold font-mono ${fundingColor}`}>
              {totalFunding >= 0 ? '+' : ''}${totalFunding.toFixed(6)}
            </span>
          </div>
        </button>

        {fundingOpen && (
          filteredFunding.length === 0 ? (
            <div className={`p-8 text-center ${t.muted} text-xs`}>
              {connected ? 'Kayıt bulunamadı.' : 'Backend bağlantısı bekleniyor...'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className={`${t.tableHeader} border-b`}>
                    {['Tarih','Saat','Sembol','Tür','Tutar'].map((h, i) => (
                      <th key={h} className={`px-4 py-2.5 font-medium ${i < 3 ? 'text-left' : 'text-right'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredFunding.map((f, idx) => {
                    const d        = new Date(f.createdTime);
                    const received = (f.fundingFee ?? 0) >= 0;
                    return (
                      <tr key={f.id || idx} className={`border-b ${t.tableRow}`}>
                        <td className={`px-4 py-2 ${t.muted}`}>{d.toLocaleDateString('tr-TR')}</td>
                        <td className={`px-4 py-2 ${t.muted}`}>{d.toLocaleTimeString('tr-TR')}</td>
                        <td className={`px-4 py-2 font-semibold ${t.gold}`}>{fmtSym(f.symbol)}</td>
                        <td className="px-4 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                            received ? 'bg-emerald-900/50 text-emerald-400' : 'bg-rose-900/50 text-rose-400'
                          }`}>{received ? 'Alındı' : 'Ödendi'}</span>
                        </td>
                        <td className={`px-4 py-2 text-right font-mono font-bold ${received ? t.positive : t.negative}`}>
                          {received ? '+$' : '-$'}{Math.abs(f.fundingFee ?? 0).toFixed(6)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
      </>}
    </div>
  );
}
